import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, ArrowRight } from 'lucide-react';
import tournamentService, { Tournament } from '../../services/tournamentService';
import { t as tr } from '../../i18n';

// ============================================
// "Kommende Turniere" – horizontale Scroll-Sektion auf der Startseite.
// Zeigt die als Nächstes anstehenden Community-Turniere, nach erstem
// Termin aufsteigend sortiert. Gleicher Karten-Stil wie HomeNewTournaments.
// ============================================

const ACCENT = '#3fced1';
const LIMIT = 10;
const MONTHS = [tr('web.service.news-service.jan'), tr('web.service.news-service.feb'), tr('web.service.news-service.maer'), tr('web.service.news-service.apr'), tr('web.service.news-service.mai'), tr('web.service.news-service.jun'), tr('web.service.news-service.jul'), tr('web.service.news-service.aug'), tr('web.service.news-service.sep'), tr('web.service.news-service.okt'), tr('web.service.news-service.nov'), tr('web.service.news-service.dez')];

interface UpcomingItem {
  tournament: Tournament;
  nextTs: number;
}

/** Kurzes deutsches Datum, z. B. "23. Aug". */
const shortDate = (ts: number): string => {
  const d = new Date(ts);
  return `${d.getDate()}. ${MONTHS[d.getMonth()]}`;
};

const locationLabel = (t: Tournament): string => {
  const city = t.address?.city?.trim();
  if (city) return city;
  const label = t.address?.label?.trim();
  if (label) return label;
  return tr('web.component.home-new-tournaments.online');
};

const HomeUpcomingTournaments: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<UpcomingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // 2026-10-06: ALLE Turniere aus dem Server-Cache – limit 500 schnitt bei > 500 Turnieren die neuesten ab
        const all: Tournament[] = await tournamentService.getAllTournamentsCached();
        const startOfToday = new Date().setHours(0, 0, 0, 0);

        const upcoming: UpcomingItem[] = all
          .filter((t) => {
            if (!t || t.visible === false || t.cancelled || !t.slug) return false;
            return Array.isArray(t.dates) && t.dates.length > 0;
          })
          .map((t) => {
            // Nächster (heutiger oder zukünftiger) Termin des Turniers
            const future = (t.dates as number[]).filter((ts) => ts >= startOfToday);
            return { tournament: t, nextTs: future.length > 0 ? Math.min(...future) : 0 };
          })
          .filter((it) => it.nextTs > 0)
          .sort((a, b) => a.nextTs - b.nextTs)
          .slice(0, LIMIT);

        if (!cancelled) setItems(upcoming);
      } catch (err) {
        console.error('Kommende Turniere konnten nicht geladen werden:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (!loading && items.length === 0) return null;

  return (
    <section aria-label={tr('web.component.home-upcoming-tournaments.kommende-turniere')}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{tr('web.component.home-upcoming-tournaments.kommende-turniere')}</h2>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => navigate('/suchen')}
            className="mr-1 hidden items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1] sm:flex"
          >
            {tr('web.component.events-filter-bar.alle')}{' '}<ArrowRight size={13} />
          </button>
          {!loading && items.length > 3 && (
            <>
              <button
                type="button"
                aria-label={tr('web.component.chats-manager.zurueck')}
                onClick={() => scrollBy(-360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                aria-label={tr('web.component.home-darts-knowledge.weiter')}
                onClick={() => scrollBy(360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[240px] w-[240px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
              />
            ))
          : items.map(({ tournament, nextTs }) => (
              <UpcomingCard
                key={tournament.id ?? tournament._id ?? tournament.slug}
                tournament={tournament}
                nextTs={nextTs}
                onClick={() => navigate(`/turnier/${tournament.slug}`)}
              />
            ))}
      </div>
    </section>
  );
};

const UpcomingCard: React.FC<{ tournament: Tournament; nextTs: number; onClick: () => void }> = ({
  tournament,
  nextTs,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const file = tournament.image ?? tournament.images?.[0] ?? null;
  const imageUrl = tournamentService.buildThumbnailUrl(file, 400);
  const showImage = imageUrl && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-[240px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
    >
      <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={imageUrl as string}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <img
              src="/icon.png"
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded-lg shadow-[0_0_24px_rgba(63,206,210,0.25)]"
            />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-2.5">
        {/* Datums-Chip über dem Titel */}
        <span
          className="mb-2 self-start rounded px-2 py-0.5 text-[10px] font-bold text-black"
          style={{ backgroundColor: ACCENT }}
        >
          {shortDate(nextTs)}
        </span>
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {tournament.name ?? tr('web.component.create-entry-modal.turnier')}
        </h3>
        <div className="mt-auto flex items-center gap-1 pt-1.5 text-[11px] text-gray-500">
          <MapPin size={11} className="shrink-0" />
          <span className="truncate">{locationLabel(tournament)}</span>
        </div>
      </div>
    </button>
  );
};

export default HomeUpcomingTournaments;
